import React from 'react';
import styled from 'styled-components';
import { Orgs } from '/imports/api/orgs';
import { strings } from '/imports/strings';
import { Publications } from '/imports/publications';
import { useFindOne, useFind, useSubscribed, useUser } from '/imports/hooks';
import { RichSelect } from '/imports/ui/RichSelect';
import { css } from '/imports/ui/css';
import { Avatar } from '/imports/ui/Avatar';
import { setOrgId } from '/imports/api/users';
import { fileHref } from '/imports/api/files';

const OrgSelectStyled = styled.div`
  display: flex;
  border-radius: ${css.inputBorderRadius};
  border: ${css.border};
  padding: 0.5rem;
  height: 1.5rem;
  input {
    background: transparent;
    border: none !important;
    padding: 0.2rem 0 !important;
    cursor: pointer;
    color: ${css.grey2};
    &::placeholder {
      color: ${css.grey2};
      position: relative;
      top: -0.15rem;
    }
    &:focus {
      border: none;
      outline: none;
    }
    &:disabled {
      background-color: ${css.white};
    }
  }
  .avatar {
    margin-right: 0.5rem;
  }
  /* Auto-width: the org name is the input's PLACEHOLDER, which never sizes an input, so an invisible copy
     of it sits in the same grid cell and sets the width; the select then fills that cell (width 0 +
     min-width 100% keeps the input's own 20-character default out of the measurement). */
  .sizer {
    display: grid; align-items: center; min-width: 8rem; max-width: 24rem;
    > * { grid-area: 1 / 1; }
    .measure { visibility: hidden; white-space: pre; padding-right: 1.5rem; }
  }
  .rich-select {
    width: 0; min-width: 100%;
    input {
      min-height: 0;
      text-overflow: ellipsis;
    }
  }
  .options {
    color: ${css.grey2};
    z-index: 10;
  }
`;

/** Org switcher dropdown for the current user's organizations. */
export const OrgSelect = () => {
  const user = useUser();
  const orgsLoading = useSubscribed(Publications.listMyOrgs);
  const orgs = useFind(() => Orgs.find(), [orgsLoading]);
  const orgId = user?.orgId;
  const org = useFindOne(() => Orgs.find({ _id: orgId }), [orgId, orgsLoading]);
  /**
   * Switches the active org and reloads the app at root.
   * @param orgId - the org to switch to
   */
  const handleSetOrgId = async (orgId: string) => {
    await setOrgId.call(orgId);
    window.location.href = '/';
    window.location.reload();
  };
  if (orgsLoading) return null;
  return (
    <OrgSelectStyled className="org-select" onClick={(e: any) => e.stopPropagation()}>
      <Avatar url={fileHref(org?.avatar)} fallback={org?.name?.substring(0, 1)} size="1.5rem" />
      <div className="sizer">
        <span className="measure" aria-hidden="true">{org?.name || strings.ChooseOrganizationFirst()}</span>
        <RichSelect
          className="rich-select"
          placeholder={org?.name || strings.ChooseOrganizationFirst()}
          options={orgs.map((o) => {
            return { value: o._id, label: o.name || o._id };
          })}
          onChange={(option) => handleSetOrgId(option.value)}
          clearable={false}
          disabled={org && orgs.length === 1}
        />
      </div>
    </OrgSelectStyled>
  );
};